import { ReactNode, useState } from 'react'

import { useMarketplaceAnalytics } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/useMarketplaceAnalytics'
import { useIntroModalState } from '@percent/cause-dashboard/common/hooks/useIntroModalState/useIntroModalState'
import {
  MarketplaceProgrammeVisitedSource,
  OriginCategory
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import type { SmartApplyPartnerConfigKey } from '@percent/cause-dashboard/constants/smartApplyPartners'
import type { NativeApplyModalContext } from '@percent/cause-dashboard/constants/nativeApply/getResolvedNativeApplyContext'
import { useNativeApply } from '@percent/cause-dashboard/common/components/nativeApply'
import { useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/useCausesDashboardAnalytics'
import type { GofundmeUserJourneysVariant } from '@percent/cause-dashboard/constants/gofundmeUserJourneys'
import { GfmInterestConfirmationModal } from '@percent/cause-dashboard/common/components/gfmInterest/GfmInterestConfirmationModal'
import { GFM_INTEREST_EVENTS } from '@percent/cause-dashboard/common/components/gfmInterest/gfmInterestEvents'

import styles from './Banner.module.scss'
import classNames from 'classnames'

type BannerCtaCommon = {
  text: string
  textColor: string
  backgroundColor: string
}

export type BannerLinkCta = BannerCtaCommon & {
  kind: 'link'
  url: string
}

export type BannerNativeApplyCta = BannerCtaCommon & {
  kind: 'native_apply'
  partnerKey: SmartApplyPartnerConfigKey
  modalContext: NativeApplyModalContext
}

export type BannerRegisterInterestCta = BannerCtaCommon & {
  kind: 'register_interest'
}

export type BannerCta = BannerLinkCta | BannerNativeApplyCta | BannerRegisterInterestCta

export type BannerProps = {
  logo: string
  image: string
  backgroundColor: string
  description: ReactNode
  descriptionColor?: 'black' | 'white'
  cta: BannerCta
  experimentVariant?: GofundmeUserJourneysVariant
  tracking?: {
    programmeName: string
    source: MarketplaceProgrammeVisitedSource
    originCategory: OriginCategory
  }
}

export function Banner({
  logo,
  image,
  backgroundColor,
  description,
  descriptionColor = 'black',
  cta,
  experimentVariant,
  tracking
}: Readonly<BannerProps>) {
  const { trackProgramVisited: track } = useMarketplaceAnalytics()
  const { track: trackEvent } = useCausesPortalAnalytics()
  const { recordGfmInterestRegistered } = useIntroModalState()
  const { openNativeApply } = useNativeApply()
  const [isInterestConfirmationOpen, setIsInterestConfirmationOpen] = useState(false)

  const trackProgramVisited = async (url: string) => {
    if (!tracking) {
      return
    }

    await track({
      source: tracking.source,
      targetUrl: url,
      programmeOfferName: tracking.programmeName,
      originCategory: tracking.originCategory
    })
  }

  const handleCTAClick = async () => {
    if (cta.kind === 'register_interest') {
      recordGfmInterestRegistered()
      trackEvent(GFM_INTEREST_EVENTS.ctaClicked, {
        variant: experimentVariant,
        element: 'carousel',
        programmeName: tracking?.programmeName
      })
      setIsInterestConfirmationOpen(true)
      return
    }

    if (cta.kind === 'native_apply') {
      // Skip the marketplace `Program visited` event for native_apply CTAs — the modal itself
      // emits `Native Apply Modal Shown` with the same tracking payload, so firing
      // `trackProgramVisited('')` here would just record a misleading empty `targetUrl`.
      openNativeApply({
        partnerKey: cta.partnerKey,
        modalContext: cta.modalContext,
        tracking: tracking
          ? {
              programmeOfferName: tracking.programmeName,
              source: tracking.source,
              originCategory: tracking.originCategory
            }
          : undefined
      })
      return
    }

    await trackProgramVisited(cta.url)
    window.open(cta.url, '_blank')
  }

  return (
    <div
      className={classNames(styles.bannerContainer, { [styles.gfmBanner]: Boolean(experimentVariant) })}
      style={{ backgroundColor }}
    >
      <div className={styles.contentContainer}>
        <img src={logo} alt="" className={styles.logo} />
        <div
          className={classNames(styles.description, {
            [styles.descriptionTextBlack]: descriptionColor === 'black',
            [styles.descriptionTextWhite]: descriptionColor === 'white',
            [styles.gfmDescription]: Boolean(experimentVariant)
          })}
        >
          {description}
        </div>
        <button
          className={styles.ctaButton}
          style={{ color: cta.textColor, backgroundColor: cta.backgroundColor }}
          onClick={handleCTAClick}
        >
          {cta.text}
        </button>
      </div>
      <img src={image} className={styles.bannerImage} alt="" />
      {cta.kind === 'register_interest' && (
        <GfmInterestConfirmationModal
          open={isInterestConfirmationOpen}
          onClose={() => setIsInterestConfirmationOpen(false)}
          isGoodstack={experimentVariant === 'goodstack_supporters' || experimentVariant === 'goodstack_tools'}
        />
      )}
    </div>
  )
}
